import React, { useState, useEffect, useRef } from 'react';

// Dropdown filter button with a checkbox popover. Designed to replace the
// previous <select> filters on the list pages. Multi-select with a deferred
// APPLY — staged selections only commit when Apply is clicked, so the table
// doesn't re-render on every checkbox toggle.
//
// Props:
//   label     — text shown on the trigger button
//   options   — [{ value, label }]
//   selected  — array of currently-selected values
//   onChange  — called with the new array on Apply or Clear
//   hideLabel — when true, the trigger renders only the count badge +
//               chevron, no text. Used when this filter is nested under
//               a parent header that already shows the column label
//               (e.g. Suppliers list SortHeader) — prevents two
//               identical "Region / Region" lines stacking up.
export default function FilterDropdown({ label, options, selected, onChange, hideLabel = false, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const ref = useRef(null);

  // Reset the staged selection from the committed one whenever the popover
  // opens, so closing without Apply discards in-flight checkbox changes.
  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  // Click-outside closes the popover without applying.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (val) => {
    setDraft(d => d.includes(val) ? d.filter(v => v !== val) : [...d, val]);
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const clear = () => {
    setDraft([]);
    onChange([]);
    setOpen(false);
  };

  const count = selected.length;

  return (
    <div className="relative" ref={ref}>
      {/* Trigger — unified header language. Inactive renders as a plain
          muted header label (matches the sortable buttons in Briefs.js);
          a small filled triangle (▾) signals the dropdown affordance.
          When a filter is active (count > 0), the trigger picks up the
          green pill chrome + count badge so the active filter is
          impossible to miss. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        // Label sizing aligned to the shared column-header register
        // used by the Suppliers SortHeader + the Briefs sortable
        // headers: text-[10px] font-bold tracking-[0.18em] uppercase.
        // Previously text-xs font-semibold tracking-[1px] which made
        // FilterDropdown labels ~20% larger than adjacent sortable
        // column headers — flagged in the 2026-06 header-styling
        // alignment review. One vocabulary across every column
        // header surface (Suppliers list, Briefs list, BriefsBoard,
        // Admin) so they can't drift again.
        className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
          count > 0
            ? 'bg-[#92D400]/10 border border-[#92D400] text-fusion-forest rounded-xl px-3 py-2'
            : 'text-fusion-mid-grey hover:text-fusion-mid-grey'
        }`}>
        {!hideLabel && <span>{label}</span>}
        {count > 0 && (
          <span className="bg-[#92D400] text-[#131313] rounded-md px-1.5 text-[10px] font-bold leading-tight">{count}</span>
        )}
        <span aria-hidden="true" className={`text-[10px] leading-none ${count > 0 ? '' : 'opacity-80'}`}>▾</span>
      </button>
      {open && (
        <div className={`absolute top-full mt-1 z-20 bg-white border border-line rounded-xl shadow-xl min-w-[220px] p-2 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          <div className="max-h-56 overflow-y-auto mb-2">
            {options.length === 0 ? (
              <p className="font-['Metropolis'] text-xs text-[#51534A] px-2 py-2">No options</p>
            ) : options.map(opt => {
              const checked = draft.includes(opt.value);
              return (
                <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                    className="w-4 h-4 accent-active-green flex-shrink-0" />
                  <span className={`font-['Metropolis'] text-xs px-2 py-0.5 transition-colors ${checked ? 'bg-[#92D400]/15 text-fusion-forest' : 'text-fusion-mid-grey'}`}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex gap-2 border-t border-line pt-2">
            <button type="button" onClick={clear} className="flex-1 font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors py-1.5">Clear</button>
            <button type="button" onClick={apply} className="flex-1 rounded-r-btn font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase bg-[#92D400] text-[#131313] hover:bg-[#6a9a00] transition-colors py-1.5">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
